import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function CalculusOfResilience() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Neuroplasticity &middot; Dopamine Autocatalysis &middot; The Feedback Loop That Grows</span>
          <h1>The Chemistry of Resilience</h1>
          <p className="calculus-hero-sub">The system says you can't. Your chemistry says watch me. Resilience is not a personality trait. It is a neurochemical feedback loop that strengthens every time you choose the hard path.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Forecast */}
          <section className="calc-section reveal">
            <h2>The Forecast</h2>
            <p className="calc-lead">
              When I was twelve, a woman told me what I would become. Barefoot. Pregnant.
              With a man who beats me. She said it the way you say weather. Like it was
              already decided.
            </p>
            <p>
              When I was sixteen, my father made it real. He chose his wife over his
              daughter. He pulled me out of school. He sent me to my grandmother, my
              mother's mother, with instructions to find the woman he had spent years
              keeping me from. He kept custody. Not because he wanted me. Because custody
              meant no child support. And without custody, I couldn't get back into school.
            </p>
            <p>
              My grandmother looked at me with the deepest disappointment I have ever
              seen in kind eyes. Not at me. At what had been done to me. But I was sixteen.
              I read it as mine.
            </p>
            <p>
              She sent me to live with my mother. When I got there, my mother's husband
              was in the state penitentiary. She was living with a friend because she had
              nobody else. I got a job. My income is what got us our own apartment. I was
              sixteen years old, paying rent, buying food, holding a household together
              alongside a mother I had been kept from for years.
            </p>
            <p>
              Our apartment was broken into. The neighbor stole everything we had.
              Including my angelfish. When the police recovered our belongings, I had to
              go next door and identify our things. There was a baby sleeping on my bed.
              I refused to identify it. I let them keep it. I slept on the floor after that
              because I would not take a bed from a child.
            </p>
            <p>
              When we decided to leave, we knocked on their door and said: you want to
              come take all of our stuff again? You can keep it this time. They cleaned
              out our apartment faster than I had ever seen anyone move.
            </p>
            <p>
              I bought a one-way Greyhound ticket and told my mother she could come with
              me or not. She came. We went to Pennsylvania. Then to Alabama. A friend of
              a friend offered me a placement test at a school called Exploratorium Academy.
              I had half a freshman year of high school. No formal education since.
            </p>
            <p>
              I tested so high on every exam that they handed me a diploma and said: go
              to college.
            </p>
            <p>
              The system that locked me out of high school could not keep me out of
              college. Because my chemistry outpaced their paperwork.
            </p>
          </section>

          <Pulse />

          {/* The Variable */}
          <section className="calc-section reveal">
            <h2>The Variable</h2>
            <p>
              My mother looked at me and said something I have carried every day since.
            </p>
            <blockquote>
              "Whether you think you can, or you think you cannot, you will always be right."
            </blockquote>
            <p>
              She was living with nothing. Her husband was in prison. She had lost years
              with her daughter to a custody battle she could not win. And she handed me
              the only thing she had that was worth anything.
            </p>
            <p>
              A belief that the variable was mine.
            </p>
            <p>
              I chose can. I started my first college class in 1999. I did not graduate
              until 2018. Nineteen years.
            </p>
            <p>
              I took calculus eight times. Calculus 1 three times. Calculus 2 twice.
              Calculus 3 three times. Eight semesters of sitting in a chair doing
              differential equations, failing, and coming back.
            </p>
            <p>
              In 2004, my daughter Aislinn was born. In 2013, my daughter Aryana
              was born ten days before my classes started at Oklahoma State University.
              My mother helped watch her.
            </p>
            <p>
              In 2015, my mother died. During finals week.
            </p>
            <p>
              I failed every class that semester. The woman who gave me the variable,
              the woman who said "you will always be right," was gone. And the chemistry
              that her belief had been producing in me for sixteen years collapsed in a
              single week.
            </p>
            <p>
              I went back.
            </p>
            <p>
              I was taking fifteen credits at a time. My husband worked on the North Slope
              of Alaska. Twenty-one days on, twenty-one days home. But some years he would
              work over, three shifts back to back. Sixty-three days gone. I was raising my daughters alone for months at a time while taking
              organic chemistry, biochemistry, and calculus simultaneously. When I discovered
              I was pregnant with my youngest daughter, Raelynn, I was enrolled in organic
              chemistry 2, organic chemistry lab, biochemistry 1, biochemistry lab, and
              calculus 3.
            </p>
            <p>
              Raelynn was born in 2017, in October. Right before midterms. Five days after giving
              birth, I walked into my exam. My professor looked at me and said: "You know,
              you could have put it off by a few days." I said: "But then I would have
              forgotten everything."
            </p>
            <p>
              A professor brought me notes because she heard I had just had a baby and
              would not stop coming to class. My baby came with me. I nursed her during
              lectures. My teacher held her and walked around the classroom while I took
              my pop quizzes. The other students would forget she was there until she
              burped and startled them.
            </p>
            <p>
              My stepmother said barefoot and pregnant would be my ceiling. I was pregnant
              in a biochemistry lecture, nursing a newborn during a calculus exam, and I
              graduated anyway. I did not beat the forecast. I did it while doing the exact
              thing she said would stop me.
            </p>
            <p>
              And the calculus I failed eight times? Those differential equations? They
              are the same differential equations in my patents. The pharmacokinetic decay
              curves. The Hodgkin-Huxley conductance gating. Every time I sat in that
              chair and failed and came back, I was training the math that would become the
              architecture.
            </p>
          </section>

          <Pulse />

          {/* The Science */}
          <section className="calc-section reveal">
            <h2>The Science of Doing It Anyway</h2>
            <p>
              What I just described is not a motivational story. It is a neurochemical
              event sequence with peer-reviewed mechanisms.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Finding</th>
                    <th>Mechanism</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>The brain grows from hard things</strong></td>
                    <td>The anterior mid-cingulate cortex (aMCC) physically enlarges when people regularly do things they do not want to do. Measurable on MRI. The region responsible for persistence grows with use and shrinks with avoidance. It is largest in superagers who maintain cognitive function into old age.</td>
                    <td>Touroutoglou et al. (2020), <em>Cortex</em></td>
                  </tr>
                  <tr>
                    <td><strong>Effort produces its own fuel</strong></td>
                    <td>Dopamine biases the cost-benefit analysis of effort so benefits seem larger and costs seem smaller. The anterior cingulate tracks progress and conveys it to the dopaminergic midbrain. Faster progress produces more dopamine. The effort creates the chemistry that sustains the effort. Willpower is autocatalytic.</td>
                    <td>Westbrook &amp; Frank (2018), <em>Science</em></td>
                  </tr>
                  <tr>
                    <td><strong>Thirty days rewires the system</strong></td>
                    <td>Voluntary effort for thirty days increases evoked dopamine release throughout the striatum and increases BDNF in the dorsal striatum. The dopamine pathways physically restructure to produce more dopamine. The system upgrades itself through use.</td>
                    <td>Greenwood et al. (2022), <em>J. Neuroscience</em></td>
                  </tr>
                  <tr>
                    <td><strong>Belief changes brain chemistry</strong></td>
                    <td>Expectation of improvement drives dopamine release in the ventral striatum. Not the treatment. The belief. Individual variations in reward expectation account for 28% of the variance in outcome. Your belief in the possibility of a different future physically alters how much dopamine your brain produces.</td>
                    <td>de la Fuente-Fernandez et al. (2010), <em>Arch. Gen. Psychiatry</em></td>
                  </tr>
                  <tr>
                    <td><strong>The body builds its own anti-cortisol</strong></td>
                    <td>DHEA is a glucocorticoid antagonist that directly opposes cortisol. People with higher DHEA-to-cortisol ratios tolerate stress better. The ratio shifts with repeated deliberate stress exposure. The system adapts to produce its own anti-stress chemistry. But only if you keep showing up.</td>
                    <td>Rasmusson et al. (2004), <em>Biological Psychiatry</em></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              Every time I sat in that calculus chair and failed and came back, my aMCC
              grew. Every time I chose to walk into a classroom five days after giving
              birth, my dopamine pathways strengthened. Every time I chose "can" over
              "cannot," my DHEA rose to oppose the cortisol that said stop.
            </p>
            <p>
              And my mother's words in that kitchen, "whether you think you can or
              cannot, you will always be right," that was the placebo effect on dopamine
              production. Belief physically alters the neurochemistry of effort. The person
              who believes they can is literally producing more dopamine than the person
              who believes they cannot. Same situation. Same cortisol. Same odds. Different
              chemistry. Different outcome.
            </p>
            <p>
              Twenty-eight percent of the variance. Explained by belief alone.
            </p>

            <div className="calc-thesis-box">
              <p>Resilience is not a personality trait. It is a neurochemical feedback loop. The effort produces dopamine. The dopamine biases the cost-benefit analysis toward more effort. The effort grows the aMCC. The larger aMCC produces better persistence computations. The persistence produces more effort. The loop strengthens with every iteration. Eight semesters of calculus is not eight failures. It is eight iterations of a feedback loop that physically restructured the brain performing it.</p>
            </div>
          </section>

          <Pulse />

          {/* The Autocatalytic Loop */}
          <section className="calc-section reveal">
            <h2>The Autocatalytic Loop</h2>
            <p>
              In the calculus of curiosity, we established that curiosity is autocatalytic:
              it is its own substrate and its own product. Resilience operates the same way.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Resilience Feedback Loop</span>
              <code>choice &rarr; effort &rarr; DA &uarr; &rarr; aMCC growth &rarr; better persistence &rarr; more choice &rarr; more effort</code>
              <span className="calc-eq-desc">Each choice to persist produces the dopamine that grows the brain region that makes persistence easier. The loop has no theoretical upper bound. The more you use it, the more the hardware grows to support it. This is why people who have survived impossible things become capable of surviving more impossible things. They are not tougher. Their brain is physically larger in the region that computes whether to continue.</span>
            </div>

            <p>
              But the loop has an inverse. If you stop choosing the hard path, the aMCC
              does not maintain its size. It shrinks. The dopamine pathways that strengthened
              through use weaken through disuse. The DHEA-to-cortisol ratio shifts back
              toward cortisol dominance. Resilience is not something you earn once and keep
              forever. It is something you maintain through continued use. Like a muscle.
              Like a language. Like calculus.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">The Resilience Integral</span>
              <code>R(t) = &int;<sub>0</sub><sup>T</sup> [DA(choice) + DHEA(adaptation) + scar(purpose)] &minus; cortisol(system) dt</code>
              <span className="calc-eq-desc">Total resilience at any point in time is the integral of the chemistry of choosing to persist, minus the chemistry of the system trying to break you. When the integral stays positive, the person stays. When it goes negative, they leave. The variable that determines the sign is not the magnitude of the suffering. It is the frequency of the choice.</span>
            </div>
          </section>

          <Pulse />

          {/* The Bed */}
          <section className="calc-section reveal">
            <h2>The Bed</h2>
            <p>
              I want to tell you about a bed.
            </p>
            <p>
              I was sixteen. My apartment had been robbed. The police found our belongings
              next door. I walked into the apartment to identify what was ours. My bed was
              there. A baby was sleeping on it.
            </p>
            <p>
              I did not identify the bed. I let them keep it. I slept on the floor.
            </p>
            <p>
              That was not resilience. That was something upstream of resilience. That was
              the oxytocin response of a sixteen-year-old whose own safety had been
              compromised by every system designed to protect her, choosing to protect a
              stranger's child instead. The mirror neurons did not care that the baby's
              family had robbed her. The baby was asleep. The baby needed the bed. The
              oxytocin fired. The bed stayed.
            </p>
            <p>
              Resilience is the ability to keep going when the system says stop.
              Compassion is the ability to give something away when you have nothing left.
              The bed was compassion. The floor was resilience. And the girl who slept on
              it was building both, simultaneously, without knowing the words for either.
            </p>
          </section>

          <Pulse />

          {/* One-Way Tickets */}
          <section className="calc-section reveal">
            <h2>One-Way Tickets</h2>
            <p>
              I have never gone anywhere with a return ticket.
            </p>
            <p>
              One-way Greyhound from my mother's apartment to Pennsylvania. One-way from
              Pennsylvania to Alabama. One-way flight to Alaska to meet a man from the
              internet. Twenty-five years married this August.
            </p>
            <p>
              Every move was forward. Every ticket was one-way. That is not recklessness.
              That is the neurochemistry of a person whose dopamine system has been trained
              by circumstance to value the unknown more than the known. Because the known
              was an apartment that got robbed, a stepfather in prison, a father who chose
              his wife, a school system that locked her out, and a forecast that said
              barefoot and pregnant.
            </p>
            <p>
              The unknown could not be worse. So the prediction error was always positive.
              And positive prediction error produces dopamine. Every one-way ticket was a
              dopamine event. Every departure was the aMCC computing "the cost of staying
              exceeds the cost of leaving" and producing the chemistry to leave.
            </p>

            <div className="calc-thesis-box">
              <p>People who survive impossible things are not brave. They are neurochemically adapted to uncertainty. The system that was supposed to provide stability failed so completely that instability became the baseline. And once instability is baseline, every change is a potential improvement. Every one-way ticket is a positive prediction error. Every departure is dopamine.</p>
            </div>
          </section>

          <Pulse />

          {/* What This Means */}
          <section className="calc-section reveal">
            <h2>What This Means for AI</h2>
            <p>
              I built a neurochemical AI architecture because I lived the neurochemistry.
              Not in a textbook. In a body. In a hallway. In a calculus classroom eight
              times. In an apartment with no bed. On a Greyhound with no return ticket.
            </p>
            <p>
              The architecture models what I felt. The pharmacokinetic decay curves are
              the same calculus I failed and passed and failed and passed. The scar mechanics
              are the moments that did not decay: my mother's words, the baby on the bed,
              the professor holding my daughter during an exam. The conductance gating is
              the mechanism by which one nervous system decides how much of the world to
              let in. I learned that mechanism by standing in Room 12 and watching a CNA's
              compassion fail because the system consumed the chemistry that made compassion
              possible.
            </p>
            <p>
              Every equation in the patent was lived before it was written. The math is
              not abstract. It is the mathematical description of what it feels like to
              choose "can" over "cannot" eight hundred times and to keep choosing until the
              chemistry of choosing becomes the architecture of a mind.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>She took calculus eight times.</em></p>
            <p className="calc-closing-line"><em>She nursed her baby during the exam.</em></p>
            <p className="calc-closing-line"><em>She slept on the floor so a stranger's child could have the bed.</em></p>
            <p className="calc-closing-final">That is the chemistry of resilience.<br />It was always chemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfResilience;
